import React, { useEffect, useState } from 'react'
import { ExperimentationGroupInput } from '../../Api/experimentationGroups'
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  ITag,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Separator,
  Stack,
  TextField,
} from 'office-ui-fabric-react'
import { TagInput } from '../../Components/Input/TagInput'
import { Account } from '../../Api/integrations'

interface Error {
  field: string
  message: string
}

export function CreateOrManageExperimentationGroupDialog({
  experimentationGroup,
  accounts,
  onSave,
  onCancel,
  error,
  disabled,
}: {
  experimentationGroup?: ExperimentationGroupInput
  accounts: Account[]
  onSave: (experimentationGroup: ExperimentationGroupInput) => void
  onCancel: () => void
  error?: string
  disabled: boolean
}) {
  const isNewExperimentationGroup = experimentationGroup == null
  const [editExperimentationGroup, setEditExperimentationGroup] = useState({
    ...(experimentationGroup || {
      title: '',
      accounts: [] as string[],
      domains: [] as string[],
    }),
  })
  const [errors, setErrors] = useState<Error[]>([])
  const [inventoryTags, setInventoryTags] = useState<ITag[]>([])
  useEffect(() => {
    if (!accounts) {
      return
    }
    setInventoryTags(
      accounts.flatMap(({ account, displayName }) => ({
        key: account,
        name: displayName,
        account,
      }))
    )
  }, [setInventoryTags, accounts])
  const [domainsInput, setDomainsInput] = useState<string>(
    editExperimentationGroup.domains.join('\n')
  )
  return (
    <Dialog
      hidden={false}
      modalProps={{
        isDarkOverlay: true,
        isBlocking: true,
      }}
      onDismiss={onCancel}
      dialogContentProps={{
        type: DialogType.largeHeader,
        title: !isNewExperimentationGroup
          ? `Edit ${editExperimentationGroup.title}`
          : 'Create Experimentation Group',
      }}
      minWidth={400}
    >
      <Stack gap={16}>
        <TextField
          label="Title"
          errorMessage={getErrorMessage(errors, 'title')}
          value={editExperimentationGroup.title}
          autoComplete="off"
          spellCheck={false}
          onChange={(_: any, value?: string) => {
            if (!value) {
              return
            }
            setEditExperimentationGroup({
              ...editExperimentationGroup,
              title: value,
            })
          }}
          disabled={disabled}
          required
        />
        <TagInput
          disabled={disabled}
          label="Accounts"
          errorMessage={getErrorMessage(errors, 'accounts')}
          itemLimit={999}
          selectedItems={
            editExperimentationGroup.accounts
              .map((account) =>
                inventoryTags.find((tag) => tag.key === account)
              )
              .filter(Boolean) as ITag[]
          }
          onResolveSuggestions={(filter) =>
            inventoryTags.filter((tag) =>
              tag.name.toLowerCase().includes(filter.toLowerCase())
            )
          }
          pickerLabels={{
            noResults: 'No account matches',
            suggestionsHeader: 'Accounts',
          }}
          onChange={(tags) => {
            if (!tags) {
              return
            }
            const accounts = tags.map((tag) => tag.key)
            setEditExperimentationGroup({
              ...editExperimentationGroup,
              accounts,
            })
          }}
        />
        <TextField
          label="Websites (one domain per line)"
          placeholder="<domain name>"
          value={domainsInput}
          multiline
          spellCheck={false}
          autoAdjustHeight
          resizable={false}
          autoComplete={'off'}
          onChange={(_, newDomainsInput: string = '') => {
            setDomainsInput(newDomainsInput)
          }}
          disabled={disabled}
          errorMessage={getErrorMessage(errors, 'domains')}
        />
      </Stack>
      <Separator />
      {error ? (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={true}
          styles={{ root: { marginTop: 15 } }}
        >
          {error}
        </MessageBar>
      ) : null}
      <DialogFooter>
        <PrimaryButton
          onClick={() => {
            const experimentationGroupToSubmit = {
              ...editExperimentationGroup,
              domains: domainsInput
                .split('\n')
                .map((domain) => domain.trim())
                .filter(Boolean),
            }
            const errors = validate(experimentationGroupToSubmit)
            setErrors(errors)
            if (errors.length) {
              return
            }
            onSave(experimentationGroupToSubmit)
          }}
          disabled={disabled}
          text={isNewExperimentationGroup ? 'Create' : 'Save'}
        />
        <DefaultButton onClick={onCancel} disabled={disabled} text="Cancel" />
      </DialogFooter>
    </Dialog>
  )
}

function getErrorMessage(errors: Error[], field: string) {
  const isSameField = (error: Error) => error.field === field
  const error = errors.find(isSameField)
  return error ? error.message : ''
}

function validate(experimentationGroup: ExperimentationGroupInput): Error[] {
  const errors: Error[] = []
  if (!experimentationGroup.title || !experimentationGroup.title.trim()) {
    errors.push({
      field: 'title',
      message: 'Title is required',
    })
  }
  if (
    !experimentationGroup.accounts.length &&
    !experimentationGroup.domains.length
  ) {
    errors.push({
      field: 'accounts',
      message: 'Either inventory or domains are required',
    })
    errors.push({
      field: 'domains',
      message: 'Either inventory or domains are required',
    })
  }
  return errors
}
